<template>
    <USlideover :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-2xl' }">
        <UForm ref="form" @submit="onSubmit" :schema="schema" :state="model" :validate-on="['submit']" class="h-full">
            <UCard class="h-full flex flex-col overflow-y-auto" :ui="{ ring: '', divide: '' }">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-base font-medium leading-6 text-gray-800">
                            Edit Segment
                        </h3>
                    </div>
                </template>
                <OverlaySegmentForm v-model="model" />
                <template #footer>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton type="submit" size="lg" :loading="loading" color="black">
                            Save
                        </UButton>
                    </div>
                </template>
            </UCard>
        </UForm>
    </USlideover>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'
import type { Filter } from '~/types/filter'
import type { BroadcastSegment } from '~/types/segment'
import { chipsToCustomerFilter, customerFilterToChips, customerFilterTemplate } from '~/utils/customer-filter-chips'
import { isEmptyFilter } from '~/utils/customer-filter'

interface PickedCustomer { id: string; number: number; name: string }
interface Model { name: string; description: string; mode: 'filtered' | 'manual'; chips: Filter[]; includes: PickedCustomer[]; excludes: PickedCustomer[] }

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
    segment?: BroadcastSegment
}>()

const form = ref()
const loading = ref(false)
const model = ref<Model>({ name: '', description: '', mode: 'filtered', chips: customerFilterTemplate(), includes: [], excludes: [] })
let loadedSourceViewId: string | null = null

const schema = z.object({
    name: z.string().min(1, 'Name is required'),
    description: z.string().optional(),
})

const client = useSupabaseClient()

watch(() => props.open, async (open) => {
    if (open && props.segment) {
        model.value = { name: '', description: '', mode: 'filtered', chips: customerFilterTemplate(), includes: [], excludes: [] }
        loadedSourceViewId = null

        const resp = await $fetch<BroadcastSegment & { members: { customer_id: string; mode: 'include' | 'exclude' }[] }>(
            `/api/segments/${props.segment.id}`
        )

        loadedSourceViewId = resp.source_view_id

        const includeIds = resp.members.filter(m => m.mode === 'include').map(m => m.customer_id)
        const excludeIds = resp.members.filter(m => m.mode === 'exclude').map(m => m.customer_id)
        const allIds = [...includeIds, ...excludeIds]

        let customerMap: Record<string, PickedCustomer> = {}
        if (allIds.length) {
            const { data } = await client
                .from('customers')
                .select('id, number, first_name, last_name')
                .in('id', allIds)
            for (const c of data ?? []) {
                customerMap[c.id] = {
                    id: c.id,
                    number: c.number,
                    name: [c.first_name, c.last_name].filter(Boolean).join(' '),
                }
            }
        }

        model.value = {
            name: resp.name,
            description: resp.description ?? '',
            mode: (isEmptyFilter(resp.filters) && includeIds.length) ? 'manual' : 'filtered',
            chips: customerFilterToChips(resp.filters, customerFilterTemplate()),
            includes: includeIds.map(id => customerMap[id] ?? { id, number: 0, name: '(unknown)' }),
            excludes: excludeIds.map(id => customerMap[id] ?? { id, number: 0, name: '(unknown)' }),
        }
    }
})

async function onSubmit(event: FormSubmitEvent<z.infer<typeof schema>>) {
    if (!props.segment) return

    loading.value = true

    try {
        await $fetch(`/api/segments/${props.segment.id}`, {
            method: 'PUT',
            body: {
                name: event.data.name,
                description: event.data.description,
                filters: model.value.mode === 'filtered' ? chipsToCustomerFilter(model.value.chips) : {},
                source_view_id: model.value.mode === 'filtered' ? loadedSourceViewId : null,
                members: model.value.mode === 'manual'
                    ? model.value.includes.map(c => ({ customer_id: c.id, mode: 'include' }))
                    : [],
            }
        })
        dismiss(true)
    } catch (error) {
        console.error('Update error:', error)
    }

    loading.value = false
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)
    if (reload) {
        emit('reload', true)
    }
}
</script>
